/**
 * Login Page - Reconflux
 *
 * Brand-aligned authentication page with email/password login.
 * "Welcome back to your sanctuary."
 */

import { useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { Logo } from '../../components/ui/Logo'
import { useAuth } from '../../providers/AuthProvider'
import { PRIVACY_URL, TERMS_URL, marketingHomeUrl } from '../../lib/marketingSite'
import { hasPostLoginRedirect, postLoginRedirect } from '../../lib/redirectTarget'

export function LoginPage() {
  const { t, i18n } = useTranslation('auth')
  const { signIn } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      const { error } = await signIn(email, password)

      if (error) {
        // Brand-aligned error messages
        if (error.message.includes('Invalid login')) {
          setError(t('login.errorInvalidCredentials'))
        } else if (error.message.includes('Email not confirmed')) {
          setError(t('login.errorEmailNotConfirmed'))
        } else {
          setError(error.message)
        }
        return
      }

      // Success. PublicRoute normally handles the redirect (it also owns the
      // pendingInvitationToken case), but when ProtectedRoute remembered a
      // specific location we go straight back to it — a one-shot URL like the
      // Revolut OAuth callback (`?code=…`) must survive the login detour
      // (#617). If the auth state has not propagated yet, ProtectedRoute simply
      // bounces back to /login carrying the same `from`, so this is safe.
      if (hasPostLoginRedirect(location.state)) {
        navigate(postLoginRedirect(location.state, '/dashboard'), { replace: true })
      }
    } catch {
      setError(t('errors.unexpected'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-dvh bg-surface-page flex flex-col">
      {/* Header */}
      <header className="p-6">
        <Logo href={marketingHomeUrl(i18n.resolvedLanguage)} />
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          {/* Welcome text */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-ink mb-2">
              {t('login.title')}
            </h1>
            <p className="text-ink-subtle text-sm">
              {t('login.subtitle')}
            </p>
          </div>

          {/* Login form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Error message */}
            {error && (
              <div className="bg-danger/10 border border-danger/30 rounded-lg px-4 py-3">
                <p className="text-danger text-sm">{error}</p>
              </div>
            )}

            {/* Email field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm text-ink-secondary mb-1.5"
              >
                {t('login.emailLabel')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
                className="w-full px-4 py-3 bg-surface-raised border border-edge/50 rounded-lg
                  text-ink placeholder-ink-muted
                  focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/30
                  transition-colors"
                placeholder={t('login.emailPlaceholder')}
              />
            </div>

            {/* Password field */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label
                  htmlFor="password"
                  className="block text-sm text-ink-secondary"
                >
                  {t('login.passwordLabel')}
                </label>
                <Link
                  to="/forgot-password"
                  className="inline-flex items-center min-h-11 md:min-h-0 text-sm text-accent-muted hover:text-accent transition-colors"
                >
                  {t('login.forgotPassword')}
                </Link>
              </div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full px-4 py-3 bg-surface-raised border border-edge/50 rounded-lg
                  text-ink placeholder-ink-muted
                  focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/30
                  transition-colors"
                placeholder={t('login.passwordPlaceholder')}
              />
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-accent text-accent-contrast font-medium rounded-lg
                hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-accent/50 focus:ring-offset-2 focus:ring-offset-surface-page
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-all"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  {t('login.submitting')}
                </span>
              ) : (
                t('login.submit')
              )}
            </button>
          </form>

          {/* Sign up link */}
          <p className="mt-6 text-center text-sm text-ink-subtle">
            {t('login.noAccount')}{' '}
            <Link
              to="/signup"
              className="text-accent hover:text-accent-muted transition-colors"
            >
              {t('login.createAccount')}
            </Link>
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="p-6 text-center">
        <p className="text-xs text-ink-muted">
          <Trans
            t={t}
            i18nKey="legal.signingIn"
            components={{
              terms: (
                <a
                  href={TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-ink transition-colors"
                />
              ),
              privacy: (
                <a
                  href={PRIVACY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-ink transition-colors"
                />
              ),
            }}
          />
        </p>
      </footer>
    </div>
  )
}
